/**
 * Links inside task text.
 *
 * A task title is whatever you typed into Todoist, and what people type into
 * Todoist includes URLs. A Google Drive link is 90-odd characters of id and
 * query string, which on a board card is an unbreakable word wider than the
 * column — so it either overflows the card or forces every card in the column
 * wider. Neither is worth it for text nobody reads.
 *
 * Two forms arrive from Todoist, and both become a short link:
 *   - `[the SOW](https://…)` — Todoist's markdown. The label is the point.
 *   - a bare `https://…`     — shown as its host, which is the part that
 *                              actually tells you where the link goes.
 *
 * The href is always the full URL; only the visible text is shortened.
 */

import type { ReactNode } from "react";

/** `[label](url)` or a bare http(s) URL. Order matters: markdown is tried first. */
const TOKEN = /\[([^\]\n]+)\]\((https?:\/\/[^\s)]+)\)|(https?:\/\/[^\s<>()[\]]+)/g;

/** The host, which is the only part of a long URL worth the width. */
export function shortLinkLabel(url: string): string {
  try {
    const host = new URL(url).hostname.replace(/^www\./, "");
    return host || url;
  } catch {
    // Not parseable — show a clipped version rather than the whole thing.
    return url.length > 32 ? `${url.slice(0, 31)}…` : url;
  }
}

/**
 * Split text into plain strings and link elements.
 *
 * Returns the original string untouched when there is nothing to link, so the
 * common case costs one regex test and allocates nothing.
 */
export function withLinks(text: string | undefined | null, key = "l"): ReactNode {
  if (!text) return text ?? "";
  TOKEN.lastIndex = 0;
  if (!TOKEN.test(text)) return text;

  TOKEN.lastIndex = 0;
  const out: ReactNode[] = [];
  let last = 0;
  let m: RegExpExecArray | null;

  while ((m = TOKEN.exec(text)) !== null) {
    if (m.index > last) out.push(text.slice(last, m.index));
    const [, mdLabel, mdUrl, bare] = m;
    const url = mdUrl ?? bare!;
    const label = mdLabel ?? shortLinkLabel(url);
    out.push(
      <a
        key={`${key}-${m.index}`}
        href={url}
        target="_blank"
        rel="noreferrer noopener"
        title={url}
        // The row or card underneath opens a drawer on click; following a link
        // should not also do that.
        onClick={(e) => e.stopPropagation()}
        style={{
          color: "var(--accent)", textDecoration: "none",
          borderBottom: "1px solid var(--accentSoft)",
          // However short the label, it must never be the thing that widens a
          // column.
          overflowWrap: "anywhere",
        }}
      >{label}<span aria-hidden style={{ fontSize: "0.85em", marginLeft: 2 }}>↗</span></a>,
    );
    last = m.index + m[0].length;
  }
  if (last < text.length) out.push(text.slice(last));
  return out;
}

/** Plain-text form, for titles used as a `title=` tooltip or in an export. */
export function stripLinks(text: string | undefined | null): string {
  if (!text) return "";
  return text.replace(TOKEN, (_m, mdLabel: string | undefined, _mdUrl, bare: string | undefined) =>
    mdLabel ?? shortLinkLabel(bare ?? ""));
}
